import { useCallback, useState } from 'react';
import { flushSync } from 'react-dom';

import { useFormContext } from '#/components/Form/FormContext';
import { useFormSubmitInterceptor } from '#/components/Form/FormSubmitInterceptor';
import { useLatest } from '#/hooks/useLatest';

import type { StripeCreditCardAdditionalData } from './StripeCreditCardAdditionalData';
import { useStripeCreateCreditCardToken } from './useStripeCreateCreditCardToken';

type Args = {
  getAdditionalStripeData: () => StripeCreditCardAdditionalData;
  onTokenCreated?: (stripeTokenId: string) => void;
};

/**
 * This function injects values `stripeTokenId` into
 * the form once the token is created.
 *
 * If failed, it returns the error.
 */
export function useStripeTokenFormInterceptor({
  getAdditionalStripeData,
  onTokenCreated,
}: Args) {
  const [stripeTokenError, setStripeTokenError] = useState<string>();
  const { updateValues } = useFormContext();
  const { createStripeCreditCardToken } = useStripeCreateCreditCardToken();

  const onTokenCreatedRef = useLatest(onTokenCreated);

  const getAdditionalStripeDataRef = useLatest(getAdditionalStripeData);

  const interceptor = useCallback(async () => {
    try {
      setStripeTokenError(undefined);

      const token = await createStripeCreditCardToken({
        additionalData: getAdditionalStripeDataRef.current(),
      });

      flushSync(() => {
        updateValues({
          stripeTokenId: token.id,
        });
      });

      onTokenCreatedRef.current?.(token.id);
    } catch (error) {
      // @ts-expect-error TS(2571): Object is of type 'unknown'.
      setStripeTokenError(error.message);
    }
  }, [
    createStripeCreditCardToken,
    getAdditionalStripeDataRef,
    onTokenCreatedRef,
    updateValues,
  ]);

  useFormSubmitInterceptor({ onIntercept: interceptor });

  return { stripeTokenError };
}
